import { TellescopeModelFromName, useTellescopeInfiniteQuery, useTellescopeQuery } from "@tellescope/tanstack-query";
import { useCallback, useMemo } from "react";
import { createIdMap } from "@tellescope/utils";

export interface UseInboxDraftsArgs {
  enabled?: boolean;
}

export type InboxDraftItem =
  | (TellescopeModelFromName<"emails"> & { channel: "Email" })
  | (TellescopeModelFromName<"sms_messages"> & { channel: "SMS" })
  | (TellescopeModelFromName<"chats"> & { channel: "Chat" });

export const useInboxDrafts = ({ enabled = true }: UseInboxDraftsArgs = {}) => {
  const smsMessagesQuery = useTellescopeInfiniteQuery({
    model: "sms_messages",
    method: "getSome",
    request: {
      limit: 1000,
      filter: {
        isDraft: true,
      },
    },
    select: (data) =>
      data.pages
        .flat()
        .map<TellescopeModelFromName<"sms_messages"> & { channel: "SMS" }>((item) => ({ ...item, channel: "SMS" })),
    enabled,
  });
  const emailsQuery = useTellescopeInfiniteQuery({
    model: "emails",
    method: "getSome",
    request: {
      limit: 1000,
      filter: {
        isDraft: true,
      },
    },
    select: (data) =>
      data.pages
        .flat()
        .map<TellescopeModelFromName<"emails"> & { channel: "Email" }>((item) => ({ ...item, channel: "Email" })),
    enabled,
  });
  const chatsQuery = useTellescopeInfiniteQuery({
    model: "chats",
    method: "getSome",
    request: {
      limit: 1000,
      filter: {
        isDraft: true,
      },
    },
    select: (data) =>
      data.pages
        .flat()
        .map<TellescopeModelFromName<"chats"> & { channel: "Chat" }>((item) => ({ ...item, channel: "Chat" })),
    enabled,
  });

  const isLoadingDrafts = smsMessagesQuery.isLoading || emailsQuery.isLoading || chatsQuery.isLoading;
  const draftsIsError = smsMessagesQuery.isError || emailsQuery.isError || chatsQuery.isError;

  const { combinedDrafts, enduserIds, chatroomIds } = useMemo<{
    combinedDrafts: InboxDraftItem[];
    enduserIds: string[];
    chatroomIds: string[];
  }>(() => {
    if (isLoadingDrafts) {
      // short circuit to not prematurely display a mis-ordered list of drafts when some are still loading
      return {
        combinedDrafts: [],
        enduserIds: [],
        chatroomIds: [],
      };
    }

    const combinedDrafts = [
      ...(smsMessagesQuery.data ?? []),
      ...(emailsQuery.data ?? []),
      ...(chatsQuery.data ?? []),
    ].sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());

    const { enduserIds, chatroomIds } = combinedDrafts.reduce(
      (acc, draft) => {
        if (draft.enduserId) {
          acc.enduserIds.add(draft.enduserId);
        }
        if (draft.channel === "Chat") {
          acc.chatroomIds.add(draft.roomId);
        }
        return acc;
      },
      { enduserIds: new Set<string>(), chatroomIds: new Set<string>() }
    );

    return {
      combinedDrafts,
      enduserIds: Array.from(enduserIds),
      chatroomIds: Array.from(chatroomIds),
    };
  }, [chatsQuery.data, emailsQuery.data, isLoadingDrafts, smsMessagesQuery.data]);

  const endusersQuery = useTellescopeQuery({
    model: "endusers",
    method: "getByIds",
    request: {
      ids: enduserIds,
    },
    enabled: !!enduserIds.length,
    select: (data) => ({
      endusers: data.matches,
      endusersMap: createIdMap(data.matches),
    }),
  });

  const getEndusers = useCallback(
    (ids?: string[] | string) => {
      const normalizedIds = ids === undefined ? [] : typeof ids === "string" ? [ids] : ids;
      return normalizedIds.reduce<TellescopeModelFromName<"endusers">[]>((acc, curr) => {
        const enduser = endusersQuery.data?.endusersMap[curr];
        if (enduser) {
          acc.push(enduser);
        }
        return acc;
      }, []);
    },
    [endusersQuery.data?.endusersMap]
  );

  const chatroomsQuery = useTellescopeQuery({
    model: "chat_rooms",
    method: "getByIds",
    request: { ids: chatroomIds },
    select: (data) => ({
      chatrooms: data.matches,
      chatroomsMap: createIdMap(data.matches),
    }),
    enabled: !!chatroomIds.length,
  });

  return {
    drafts: combinedDrafts,
    isLoadingDrafts,
    draftsIsError,
    endusers: endusersQuery.data,
    isLoadingEndusers: endusersQuery.isLoading,
    endusersIsError: endusersQuery.isError,
    chatrooms: chatroomsQuery.data,
    chatroomsIsLoading: chatroomsQuery.isLoading,
    chatroomsIsError: chatroomsQuery.isError,
    isLoading: isLoadingDrafts || endusersQuery.isLoading || chatroomsQuery.isLoading,
    isError: draftsIsError || endusersQuery.isError || chatroomsQuery.isError,
    getEndusers,
  };
};
